// Builds a stamped long-shadow behind a heading, sized to reach the slide edge plus an overshoot.
// The shadow is a repeated text-shadow rather than an SVG filter: filters are the thing that broke
// on iOS Safari in the original prototype, and stamping renders identically everywhere.
(() => {
  const DIRECTIONS = {
    'down-right': [0.7071, 0.7071],
    'down-left': [-0.7071, 0.7071],
    'up-right': [0.7071, -0.7071],
    'up-left': [-0.7071, -0.7071],
  };
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const numberFrom = (styles, property, fallback) => {
    const value = Number(styles.getPropertyValue(property).replace('px', '').trim());
    return Number.isFinite(value) && value !== 0 ? value : fallback;
  };

  const distanceToEdge = (rect, boundsRect, x, y) => {
    const distances = [];
    if (x > 0) distances.push((boundsRect.right - rect.left) / x);
    if (x < 0) distances.push((boundsRect.left - rect.right) / x);
    if (y > 0) distances.push((boundsRect.bottom - rect.top) / y);
    if (y < 0) distances.push((boundsRect.top - rect.bottom) / y);
    const usable = distances.filter((d) => Number.isFinite(d) && d > 0);
    return usable.length ? Math.min(...usable) : 0;
  };

  const measure = (wrapper) => {
    const layer = wrapper.querySelector('[data-long-shadow-layer]');
    const bounds = wrapper.closest('.slideshow__slide, [data-long-shadow-bounds]') || wrapper.parentElement;
    if (!layer || !bounds) return null;

    const styles = getComputedStyle(wrapper);
    const [rawX, rawY] = DIRECTIONS[wrapper.dataset.direction] || DIRECTIONS['down-right'];
    const colour = styles.getPropertyValue('--long-shadow-color').trim() || '#16ace3';
    const overshoot = numberFrom(
      styles,
      mobileViewport.matches ? '--long-shadow-overshoot-mobile' : '--long-shadow-overshoot-desktop',
      80
    );
    const reach = distanceToEdge(wrapper.getBoundingClientRect(), bounds.getBoundingClientRect(), rawX, rawY);
    // text-shadow casts the bare glyph, but the visible letter is fattened by its stroke, so the
    // trailing cast would sit narrower than the letter it comes from. Sweeping each stamp
    // perpendicular by the stroke radius widens the cast to match the outline.
    const stroke = parseFloat(getComputedStyle(layer).webkitTextStrokeWidth) || 0;
    // On mobile only the outline is drawn, because a trail across a narrow slide covers the artwork.
    return { layer, rawX, rawY, colour, stroke, trail: !mobileViewport.matches,
             length: Math.max(0, reach + overshoot), travel: Math.min(120, Math.max(32, reach * 0.12)) };
  };

  // 0.8px steps keep the cast solid without emitting more shadows than the browser can paint.
  const STAMP_STEP = 0.8;

  // Offsetting a glyph copy around a circle is a Minkowski sum with a disc, which dilates the
  // letterform with genuinely rounded corners. -webkit-text-stroke can only mitre, and the SVG
  // filter that would round it is what broke iOS Safari in the prototype.
  const ring = (radius, steps = 16) => {
    if (radius <= 0.5) return [[0, 0]];
    const points = [[0, 0]];
    for (let i = 0; i < steps; i += 1) {
      const angle = (i / steps) * Math.PI * 2;
      points.push([Math.cos(angle) * radius, Math.sin(angle) * radius]);
    }
    return points;
  };

  const paint = (wrapper, metrics, progress) => {
    const visible = metrics.trail ? metrics.length * progress : 0;
    const radius = metrics.stroke / 2;
    const outline = ring(radius);
    const perpX = -metrics.rawY;
    const perpY = metrics.rawX;
    const stamps = [];

    // Rounded outline around the letterform itself.
    for (const [ox, oy] of outline) {
      stamps.push(`${ox.toFixed(2)}px ${oy.toFixed(2)}px 0 ${metrics.colour}`);
    }

    // The trail, swept perpendicular so it matches the width of that outline.
    const spread = radius > 0.5 ? [-radius, 0, radius] : [0];
    for (let distance = STAMP_STEP; distance <= visible; distance += STAMP_STEP) {
      const baseX = metrics.rawX * distance;
      const baseY = metrics.rawY * distance;
      for (const offset of spread) {
        stamps.push(`${(baseX + perpX * offset).toFixed(2)}px ${(baseY + perpY * offset).toFixed(2)}px 0 ${metrics.colour}`);
      }
    }

    metrics.layer.style.textShadow = stamps.length ? stamps.join(',') : 'none';
    const remaining = metrics.travel * (1 - progress);
    wrapper.style.transform = `translate3d(${(metrics.rawX * remaining).toFixed(2)}px, ${(metrics.rawY * remaining).toFixed(2)}px, 0)`;
  };

  const build = (wrapper) => {
    const metrics = measure(wrapper);
    if (!metrics || (metrics.trail && !metrics.length)) return;
    wrapper.classList.add('is-cast');

    const duration = Number(wrapper.dataset.duration) || 900;
    if (prefersReducedMotion.matches || wrapper.dataset.played === 'true') {
      paint(wrapper, metrics, 1);
      wrapper.style.transform = '';
      return;
    }
    wrapper.dataset.played = 'true';

    let start = null;
    const tick = (now) => {
      if (start === null) start = now;
      const progress = Math.min((now - start) / duration, 1);
      paint(wrapper, metrics, 1 - Math.pow(1 - progress, 3));
      if (progress < 1) {
        window.requestAnimationFrame(tick);
      } else {
        wrapper.style.transform = '';
      }
    };
    window.requestAnimationFrame(tick);
  };

  const buildAll = () => document.querySelectorAll('[data-long-shadow]').forEach(build);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildAll, { once: true });
  } else {
    buildAll();
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll('[data-long-shadow]').forEach((wrapper) => {
        const metrics = measure(wrapper);
        if (metrics && (metrics.length || !metrics.trail)) paint(wrapper, metrics, 1);
      });
    }, 200);
  });

  document.addEventListener('shopify:section:load', buildAll);
})();
